/* eslint-disable react/forbid-prop-types */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { toast } from 'react-toastify';

import Modal from '../../../../components/Modal/Modal';
import { SmallButton } from '../../../../styles/ui/Button';
import {
   mapFieldErrors,
   firstErrorCode,
} from '../../../../services/routing/routingScheduleApi';

// =============================================================================
// AssignTechnicianModal — DESIGNA (ou remove) o técnico de uma rota (handoff
// 0042 §3 — renomeado de "atribuir" p/ "designar": vincula o técnico à ROTA, no
// planejamento; a propagação aos itens é o "Atribuir rota", §4).
//
// Regra 1-técnico-1-rota: técnicos já designados a OUTRA rota do schedule vêm
// com isAssigned=true e ficam desabilitados (o técnico atual desta rota
// continua selecionável/destacado). Clicar num técnico designa na hora;
// "Remover técnico" desdesigna (assigned_technician_employee_id: null) → volta a
// rascunho. Erros 422 (errors[].code) viram toast: route_locked (rota
// atribuída — reverter antes) / commission_locked (remover comissões antes) /
// technician_already_in_route / technician_not_eligible / technician_not_found /
// invalid_vehicle_param.
// =============================================================================

const Intro = styled.div`
   font-size: 0.85rem;
   color: ${({ theme }) => theme?.colors?.textSecondary ?? '#555'};
   margin-bottom: 10px;
   line-height: 1.4;

   strong {
      color: ${({ theme }) => theme?.colors?.textPrimary ?? '#111'};
   }
`;

const List = styled.div`
   display: flex;
   flex-direction: column;
   gap: 6px;
   max-height: 360px;
   overflow-y: auto;
`;

const TechRow = styled.button`
   display: flex;
   align-items: center;
   gap: 10px;
   width: 100%;
   text-align: left;
   padding: 8px 10px;
   border-radius: 10px;
   border: 1px solid
      ${({ $current }) =>
         $current ? 'rgba(31,31,24,0.6)' : 'rgba(0, 0, 0, 0.12)'};
   background: ${({ $current }) => ($current ? 'rgba(31,31,24,0.05)' : '#fff')};
   cursor: pointer;

   &:hover:not(:disabled) {
      border-color: rgba(0, 0, 0, 0.35);
      background: rgba(0, 0, 0, 0.03);
   }

   &:disabled {
      cursor: not-allowed;
      opacity: 0.5;
   }

   .av {
      width: 30px;
      height: 30px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 0.7rem;
      font-weight: 700;
      color: #fff;
      background: #6b7280;
      flex-shrink: 0;
   }

   .info {
      display: flex;
      flex-direction: column;
      min-width: 0;
   }

   .nm {
      font-size: 0.85rem;
      font-weight: 600;
      color: #1f1f18;
   }

   .sub {
      font-size: 0.72rem;
      color: #888;
   }

   .tag {
      margin-left: auto;
      font-size: 0.68rem;
      font-weight: 700;
      color: #92400e;
   }

   .tag.current {
      color: #1f1f18;
   }
`;

const RemoveButton = styled(SmallButton).attrs({ $intent: 'danger' })`
   margin-bottom: 12px;
   width: 100%;
`;

function reportError(err) {
   const code = firstErrorCode(err);
   const byCode = {
      route_locked:
         'Rota atribuída — reverta a atribuição antes de trocar o técnico.',
      commission_locked:
         'Há comissão lançada nos itens. Remova as comissões antes de trocar o técnico.',
      technician_already_in_route:
         'Este técnico já está em outra rota deste período.',
      technician_not_eligible: 'Funcionário não elegível como técnico.',
      technician_not_found: 'Funcionário não encontrado.',
      invalid_vehicle_param: 'Veículo inexistente ou inativo.',
   };
   const fieldMessages = Object.values(mapFieldErrors(err));
   const apiMessage = err?.response?.data?.message;
   toast.error(
      byCode[code] ??
         fieldMessages[0] ??
         apiMessage ??
         'Não foi possível atualizar o técnico.'
   );
}

export default function AssignTechnicianModal({
   subcluster,
   technicians,
   onClose,
   onSubmit,
}) {
   const [isSubmitting, setIsSubmitting] = useState(false);
   const currentId = subcluster.technician?.employeeId ?? null;

   const submit = async (employeeId) => {
      setIsSubmitting(true);
      try {
         await onSubmit(subcluster.subclusterId, employeeId);
         // Sucesso: o pai fecha o modal e recarrega o snapshot.
      } catch (err) {
         reportError(err);
         setIsSubmitting(false);
      }
   };

   return (
      <Modal
         isOpen
         onClose={isSubmitting ? () => {} : onClose}
         title={`Designar técnico — ${subcluster.label}`}
         maxWidth="440px"
      >
         <Intro>
            Designe um técnico para a rota <strong>{subcluster.label}</strong>.
            Um técnico só pode estar em uma rota por período — os já alocados
            aparecem desabilitados. Designar é só o planejamento; depois clique em{' '}
            <strong>Atribuir rota</strong> para vincular o técnico aos serviços.
         </Intro>

         {currentId != null && (
            <RemoveButton
               type="button"
               onClick={() => submit(null)}
               disabled={isSubmitting}
            >
               Remover técnico desta rota (volta a rascunho)
            </RemoveButton>
         )}

         <List>
            {technicians.map((tech) => {
               const isCurrent = tech.employeeId === currentId;
               const disabled =
                  isSubmitting || (tech.isAssigned && !isCurrent);
               return (
                  <TechRow
                     key={tech.employeeId}
                     type="button"
                     $current={isCurrent}
                     disabled={disabled}
                     onClick={() => submit(tech.employeeId)}
                  >
                     <span className="av">{tech.init}</span>
                     <span className="info">
                        <span className="nm">{tech.name}</span>
                        {tech.department?.department_name && (
                           <span className="sub">
                              {tech.department.department_name}
                           </span>
                        )}
                     </span>
                     {isCurrent ? (
                        <span className="tag current">Atual</span>
                     ) : (
                        tech.isAssigned && (
                           <span className="tag">Em outra rota</span>
                        )
                     )}
                  </TechRow>
               );
            })}
            {technicians.length === 0 && (
               <Intro>Nenhum técnico elegível neste agendamento.</Intro>
            )}
         </List>
      </Modal>
   );
}

AssignTechnicianModal.propTypes = {
   subcluster: PropTypes.object.isRequired,
   technicians: PropTypes.array.isRequired,
   onClose: PropTypes.func.isRequired,
   onSubmit: PropTypes.func.isRequired,
};
